import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/api/firebase/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCohort } from "@/hooks/cohort/useUserCohort";
import {
  useTransactionViewStore,
  viewScopeKey,
} from "@/store/transactionViewStore";

interface TransactionViewSwitchProps {
  /** Set when a trainer is viewing a specific cohort's course. */
  cohortId?: string;
}

/**
 * Lets one person move between the cohort's primary transactions and its
 * custom set. The trainer's setting switches the whole cohort at once; this
 * switch is a personal override on top of it, so choosing "Primary" here does
 * not pull anyone else back.
 *
 * Progress for the two sets lives in separate fields on the user's progress
 * document, so switching shows the other set exactly as they left it.
 *
 * This deliberately reads the cohort directly rather than going through
 * useUserProgress — the header only needs the cohort's mode, and mounting the
 * progress hook here would add a second progress-document reader (and its
 * initialise-if-missing write) to every page that shows the header.
 */
const TransactionViewSwitch: React.FC<TransactionViewSwitchProps> = ({
  cohortId,
}) => {
  const { currentUser } = useAuth();
  const { cohort } = useUserCohort(cohortId);
  const [cohortViewMode, setCohortViewMode] = useState<"default" | "custom">(
    "default"
  );

  const scopeKey = viewScopeKey(currentUser?.id, cohort?.id);
  const viewOverride = useTransactionViewStore((s) =>
    scopeKey ? s.overrides[scopeKey] ?? null : null
  );
  const setViewOverride = useTransactionViewStore((s) => s.setViewOverride);

  // Follow the trainer's setting live, so the switch appears as soon as they
  // move the cohort onto the custom transactions.
  useEffect(() => {
    if (!cohort?.id) return;
    const unsubscribe = onSnapshot(
      doc(db, "cohorts", cohort.id),
      (snap) => {
        if (!snap.exists()) return;
        setCohortViewMode(snap.data()?.transactionViewMode || "default");
      },
      (error) => {
        console.error("Error watching the cohort view mode:", error);
        setCohortViewMode("default");
      }
    );
    return () => unsubscribe();
  }, [cohort?.id]);

  const hasPrimary =
    (cohort?.transactionConfig?.default?.transactions?.length ?? 0) > 0;
  const hasCustom =
    (cohort?.transactionConfig?.custom?.transactions?.length ?? 0) > 0;

  // Only offered once the trainer has moved the cohort onto the custom set —
  // before that, the custom transactions aren't meant to be visible yet.
  if (!scopeKey || cohortViewMode !== "custom" || !hasPrimary || !hasCustom)
    return null;

  const viewMode = viewOverride ?? cohortViewMode;
  const options: { mode: "default" | "custom"; label: string }[] = [
    { mode: "default", label: "Primary" },
    { mode: "custom", label: "Custom" },
  ];

  return (
    <div
      className="flex items-center rounded-lg border border-gray-300 p-0.5"
      role="group"
      aria-label="Transaction set"
    >
      {options.map((option) => {
        const isActive = viewMode === option.mode;
        return (
          <button
            key={option.mode}
            type="button"
            aria-pressed={isActive}
            title={`Show the ${option.label.toLowerCase()} transactions`}
            // Picking the cohort's own mode clears the override, so the user
            // follows the trainer again if they switch the cohort later.
            onClick={() =>
              setViewOverride(
                scopeKey,
                option.mode === cohortViewMode ? null : option.mode
              )
            }
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              isActive
                ? "bg-sky-600/10 text-sky-700"
                : "text-[#98A2B3] hover:text-gray-600"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default TransactionViewSwitch;
